import Image from 'next/image'
import { useState } from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'

import { Text } from '@wartek-id/text'
import { Link } from '@wartek-id/link'
import { Shimmer } from '@wartek-id/shimmer'
import { Button } from '@wartek-id/button-v1'

import { useAPIGetCategories } from 'api/inspirations/useAPIInspirations'
import { Alert } from 'components/Alert'
import HeaderBadge from './HeaderBadge'

import type { FC } from 'react'

const MAX_ROWS = 3
const MAX_COLUMNS = 3
const HIGHLIGHT_BANNER = {
  url: '',
  about: '',
  link: '',
}

const Header: FC = () => {
  const router = useRouter()
  const [categoriesObj, setCategoriesObj] = useState<any>({})

  const { data, isLoading, ...api } = useAPIGetCategories(null, {
    onSuccess: (res) => {
      // Filter to only diplay row 1-3
      const unordered = res.data.filter(
        (value) => !!value.title && value.displayRow <= MAX_ROWS
      )

      // Sort values by displayRow and displayRowOrder
      const ordered = [...unordered].sort(
        (a, b) =>
          a.displayRow - b.displayRow || a.displayRowOrder - b.displayRowOrder
      )

      // Form as an object
      const categories = ordered.reduce(
        (obj, category) => ({
          ...obj,
          [category.displayRow]: obj[category.displayRow]
            ? {
                ...obj[category.displayRow],
                [category.displayRowOrder]: category,
              }
            : {
                [category.displayRowOrder]: category,
              },
        }),
        {}
      )

      setCategoriesObj(categories)
    },
  })

  const showTime = ({ rowIndex, columnLength, columnIndex }) => {
    if (
      rowIndex === Object.keys(categoriesObj).length - 1 && // Apply to the last row
      rowIndex >= 1 && // Apply to the second row or more (hide if only 1 row available)
      columnIndex === columnLength - 1 &&
      columnIndex >= MAX_COLUMNS - 1
    )
      return true

    return false
  }

  return (
    <>
      <div className="bg-[#0036A3] flex flex-col">
        <div className="flex items-center px-4 py-4 pt-6 space-x-3 justify-items-center">
          <div>
            <Image
              layout="fixed"
              src="/icon/ide-praktik.svg"
              alt=""
              width={60}
              height={60}
            />
          </div>
          <div className="flex flex-col items-start space-y-1">
            <Text color="inverse" variant="heading-md">
              Ide Praktik
            </Text>
            <Text color="inverse" variant="body-sm">
              Artikel dan video terpilih untuk membantu Ibu Bapak Guru praktik
              pembelajaran terpusat pada murid.
            </Text>
          </div>
        </div>

        {/* Category */}
        {!isLoading && data?.data?.length >= 1 && (
          <div className="flex items-center justify-between px-4 pt-2 pb-3 space-x-3 justify-items-center">
            <Text variant="body-sm" color="inverse">
              Pilih kategori
            </Text>
            <NextLink href="/ide-praktik/kategori" passHref>
              <Link
                tabIndex={0}
                underline
                variant="white"
                className="text-xs font-semibold"
                data-testid="category-show-all-link"
              >
                Lihat semua
              </Link>
            </NextLink>
          </div>
        )}

        {api.isError && (
          <div className="mx-4">
            <Alert color="orange-10" icon="" className="my-1 !p-[10px]">
              Gagal menampilkan kategori. Harap coba lagi, atau langsung lihat
              semua kategori.
            </Alert>
            <div className="my-3">
              <Button
                color="white"
                size="sm"
                className="!bg-transparent !text-inverse hover:!bg-gray-5 hover:!text-gray-90"
                data-testid="category-show-all-button"
                onClick={() => {
                  router.push(`/ide-praktik/kategori`)
                }}
              >
                Lihat semua kategori
              </Button>
            </div>
          </div>
        )}

        {/* Category loading */}
        {isLoading && (
          <>
            <div className="flex items-center justify-between px-4 pt-2 pb-3 space-x-3 justify-items-center">
              {Array.from({ length: 2 })?.map((_, index) => (
                <Shimmer
                  height={13 - index}
                  noOfLines={1}
                  key={`loading-shimmer-${index}`}
                  width="25%"
                />
              ))}
            </div>
            <div
              className="px-4 from-[#0036A3] to-[#446FC3] bg-gradient-to-b pb-1 overflow-x-hidden"
              style={{ margin: '-1px 0 1px' }}
            >
              <div
                className="flex flex-row flex-wrap items-center content-center justify-start"
                style={{ width: '125vw' }}
              >
                {Array.from({ length: 6 })?.map((_, index) => (
                  <Shimmer
                    height={26}
                    noOfLines={1}
                    key={`loading-shimmer-item-${index}`}
                    width={`${
                      index % 3 === 0 ? '35' : index % 3 === 1 ? 40 : '45'
                    }%`}
                    className="mr-2 mb-2 !rounded-2xl"
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {!isLoading && (
        <>
          {/* If has data */}
          {data?.data?.length >= 1 && (
            <div
              className="px-4 from-[#0036A3] to-[#446FC3] bg-gradient-to-b pb-1 overflow-x-auto"
              style={{ margin: '-1px 0 1px' }}
            >
              <div className="flex flex-col flex-wrap pb-2 space-y-2">
                {Object.keys(categoriesObj).map((rowKey, rowIndex) => {
                  const row = categoriesObj[rowKey]
                  const rowKeys = Object.keys(row)
                  return (
                    <div
                      className="flex space-x-2"
                      data-testid={`category-row-${rowIndex + 1}`}
                      key={`category-${rowIndex}`}
                    >
                      {rowKeys.map((columnKey, columnIndex) => {
                        const cat = row[columnKey]
                        const testId = `category-row-${rowIndex + 1}-col-${
                          columnIndex + 1
                        }`

                        return (
                          <div
                            className="flex-none"
                            key={`category-${rowIndex}-${columnIndex}`}
                          >
                            <div
                              className="flex space-x-2"
                              data-testid={testId}
                            >
                              {/* Basic category clickable badge */}
                              <HeaderBadge
                                label={cat?.title}
                                href={`/ide-praktik/kategori/${cat?.slug}`}
                                className="!bg-blue-10"
                              />

                              {/* Show All clickable badge */}
                              {showTime({
                                rowIndex,
                                columnLength: rowKeys.length,
                                columnIndex,
                              }) && (
                                <div
                                  className="!mr-2"
                                  data-testid="category-show-all-badge"
                                >
                                  <HeaderBadge
                                    label="Lihat semua"
                                    href="/ide-praktik/kategori"
                                    className="!bg-surface"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {String(HIGHLIGHT_BANNER.url).length > 0 && (
            <div className="flex flex-col gap-3 p-4">
              <a href={HIGHLIGHT_BANNER.link} target="_blank" rel="noreferrer">
                <img alt={HIGHLIGHT_BANNER.about} src={HIGHLIGHT_BANNER.url} />
              </a>
            </div>
          )}
        </>
      )}
    </>
  )
}
export default Header
